'use client';
import { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, MessageCircle, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { contactApi } from '@/lib/api';

const WHATSAPP = process.env.NEXT_PUBLIC_WHATSAPP_NUMBER || '923001234567';
const EMAIL = process.env.NEXT_PUBLIC_EMAIL || 'your@email.com';

export default function Contact() {
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    try {
      await contactApi.send(formData);
      setStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '' });
      setTimeout(() => setStatus('idle'), 3000);
    } catch {
      setStatus('error');
      setTimeout(() => setStatus('idle'), 3000);
    }
  };

  return (
    <section id="contact" className="section" style={{ background: 'var(--bg-surface)' }}>
      <div className="container">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
          <div className="accent-line" />
          <h2 className="section-title">Get In Touch</h2>
          <p className="section-subtitle">Let&apos;s build something great together</p>
        </motion.div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr', gap: '3rem' }} className="contact-grid">
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.2 }}>
            <div className="card" style={{ marginBottom: '1.5rem', background: 'linear-gradient(135deg, var(--bg-card), var(--bg-primary))' }}>
              <div style={{ width: '50px', height: '50px', borderRadius: '12px', background: 'var(--bg-surface)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent)', marginBottom: '1rem' }}><MessageCircle size={24} /></div>
              <h3 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '1.2rem', marginBottom: '0.5rem' }}>WhatsApp</h3>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginBottom: '1rem' }}>Fastest way to reach me for project inquiries or just to say hi.</p>
              <a href={`https://wa.me/${WHATSAPP}`} target="_blank" rel="noopener noreferrer" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }}>Message on WhatsApp</a>
            </div>
            <div className="card">
              <div style={{ width: '50px', height: '50px', borderRadius: '12px', background: 'var(--bg-surface)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent)', marginBottom: '1rem' }}><Mail size={24} /></div>
              <h3 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '1.2rem', marginBottom: '0.5rem' }}>Email</h3>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginBottom: '1rem' }}>Prefer email? Drop me a line and I&apos;ll get back to you soon.</p>
              <a href={`mailto:${EMAIL}`} className="btn btn-outline" style={{ width: '100%', justifyContent: 'center' }}>{EMAIL}</a>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.3 }}>
            <div className="card" style={{ padding: '2rem' }}>
              <h3 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '1.5rem', marginBottom: '1.5rem' }}>Send a Message</h3>
              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }} className="form-row">
                  <input required placeholder="Your Name" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} style={inputStyle} />
                  <input required type="email" placeholder="Your Email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} style={inputStyle} />
                </div>
                <input required placeholder="Subject" value={formData.subject} onChange={e => setFormData({ ...formData, subject: e.target.value })} style={inputStyle} />
                <textarea required placeholder="Your Message" value={formData.message} onChange={e => setFormData({ ...formData, message: e.target.value })} style={{ ...inputStyle, minHeight: '150px', resize: 'vertical' }} />
                <button type="submit" disabled={status === 'loading'} className="btn btn-primary" style={{ alignSelf: 'flex-start', padding: '0.8rem 2rem', opacity: status === 'loading' ? 0.7 : 1 }}>
                  {status === 'loading' ? 'Sending...' : status === 'success' ? <><CheckCircle size={18} /> Sent!</> : status === 'error' ? <><AlertCircle size={18} /> Error</> : <><Send size={18} /> Send Message</>}
                </button>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
      <style>{`
        @media (max-width: 768px) { .contact-grid { grid-template-columns: 1fr !important; gap: 2rem !important; } .form-row { grid-template-columns: 1fr !important; } }
      `}</style>
    </section>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '0.9rem 1.25rem', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-surface)', color: 'var(--text-primary)', fontFamily: 'Inter, sans-serif', fontSize: '0.95rem', outline: 'none', transition: 'all 0.2s ease',
};
